import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Modal, DragAndDrop } from '@ann/web-components';
import { useMediaQueries } from '../../../../../utils/mediaQueries';
import { useStore } from '../../../../../hooks';
import { useFileUpload } from '../../hooks/use-file-upload.hook';
import { useToast } from '../../../../../components/toast';
import { attachFiletoKnowledge } from '../../services/knowledge';
import { ModalBodyContent } from './UploadFactsModal.styled';

const UploadFactsModal = observer(({
  isOpen,
  onClose,
  knowledgeBase,
  onUploadStart,
}) => {
  const navigate = useNavigate();
  const { isMobile, isIpadAirPortrait, isIpadProPortrait } = useMediaQueries();
  const { drivesStore } = useStore();
  const toast = useToast();
  const [uploadError, setUploadError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const filesToUploadRef = useRef(null);
  
  // Memoize modal width calculation
  const modalWidth = useMemo(() => {
    if (isMobile || isIpadAirPortrait || isIpadProPortrait) {
      return '95%';
    }
    return '600px';
  }, [isMobile, isIpadAirPortrait, isIpadProPortrait]);

  // Memoize knowledgeBaseId to avoid recalculation
  const knowledgeBaseId = useMemo(() => {
    return knowledgeBase?.knowledgebase_uuid || knowledgeBase?.id;
  }, [knowledgeBase?.knowledgebase_uuid, knowledgeBase?.id]);

  // Load drives when modal opens if not already loaded
  useEffect(() => {
    if (!isOpen) return;
    
    if (drivesStore && (!drivesStore.drives || drivesStore.drives.length === 0) && !drivesStore.isLoading) {
      drivesStore.fetchDrives().catch((error) => {
        console.error('Error loading drives:', error);
      });
    }
  }, [isOpen, drivesStore]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      filesToUploadRef.current = null;
      setUploadError(null);
      setIsProcessing(false);
    }
  }, [isOpen]);

  // Memoize navigation function
  const navigateToTrainProgress = useCallback(() => {
    if (knowledgeBaseId) {
      navigate(`/v2/knowledge/knowledge-hub/${knowledgeBaseId}/train-progress`, {
        state: { knowledgeName: knowledgeBase?.name },
      });
    }
  }, [navigate, knowledgeBaseId, knowledgeBase?.name]);

  const handleCloseAndNavigate = useCallback(() => {
    onClose();
    onUploadStart?.();
    setTimeout(navigateToTrainProgress, 100);
  }, [onClose, onUploadStart, navigateToTrainProgress]);

  const handleUploadStart = useCallback(() => {
    setUploadError(null);
    setIsProcessing(true);
  }, []);

  const handleUploadError = useCallback((error) => {
    setUploadError(error?.message || 'Error uploading files. Please try again.');
    setIsProcessing(false);
    console.error('Error uploading files:', error);
  }, []);

  const {
    fileInputProps,
    openFileSelector,
    processFiles,
  } = useFileUpload({
    onUploadStart: handleUploadStart,
    onUploadComplete: () => {}, // No-op since modals are already closed
    folderId: knowledgeBase?.id,
  });

  // Handle complete upload + attach process
  const handleUploadAndAttach = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    setUploadError(null);
    setIsProcessing(true);
    filesToUploadRef.current = files;

    let uploadResult = null;
    let documentUuids = [];

    try {
      // Step 1: Upload files
      uploadResult = await processFiles(files);

      if (!uploadResult || uploadResult.successful === 0) {
        throw new Error('No files were uploaded successfully.');
      }

      // Step 2: Extract document UUIDs from successful uploads
      documentUuids = uploadResult.results
        .filter(result => result && result.success && result.documentUuid)
        .map(result => result.documentUuid);

      if (documentUuids.length === 0) {
        throw new Error('No valid document UUIDs found from uploaded files.');
      }

      // Step 3: Attach files to knowledge base
      try {
        const attachResponse = await attachFiletoKnowledge({
          items: documentUuids.map(uuid => ({ document_uuid: uuid })),
        });

        // Step 4: Handle success
        if (attachResponse.status_code === 200) {
          // Clear any existing toasts (from upload) before showing attach success
          toast.clear();
          
          const attachedCount = attachResponse.body?.attached?.length || documentUuids.length;
          toast.success({
            title: 'Success',
            message: `${attachedCount} file(s) uploaded and attached successfully.`,
          });

          // Close modal and navigate on success
          handleCloseAndNavigate();
        } else {
          // Handle backend error (status_code 40X in response body)
          // Files were uploaded successfully but attach failed
          // Clear any existing toasts before showing error
          toast.clear();
          
          toast.error({
            title: 'Files Uploaded',
            message: 'Documents could not be linked to the knowledge base. Please try linking them later.',
          });
          setUploadError('Documents could not be linked to the knowledge base. Please try linking them later.');
          setIsProcessing(false);
        }
      } catch (attachError) {
        // Attach failed - but upload was successful
        // Clear any existing toasts before showing error
        toast.clear();
        
        toast.error({
          title: 'Files Uploaded',
          message: 'Documents could not be linked to the knowledge base. Please try linking them later.',
        });
        setUploadError('Documents could not be linked to the knowledge base. Please try linking them later.');
        setIsProcessing(false);
      }
    } catch (error) {
      // Handle network errors or other exceptions
      // Check if upload was successful by checking if we have documentUuids
      // This means we passed the upload step but attach failed
      // Also check uploadResult to be sure
      const uploadSucceeded = uploadResult && 
                              uploadResult.successful > 0 && 
                              documentUuids.length > 0;
      
      // Clear any existing toasts before showing error
      toast.clear();
      
      if (uploadSucceeded) {
        // Upload was successful but attach failed (network error, etc)
        toast.error({
          title: 'Files Uploaded',
          message: 'Documents could not be linked to the knowledge base. Please try linking them later.',
        });
        setUploadError('Documents could not be linked to the knowledge base. Please try linking them later.');
        setIsProcessing(false);
      } else {
        // Upload failed - check if it's the specific error we throw
        // If error message matches upload failure, it's an upload error
        // Otherwise it might be an attach error that happened before we could extract UUIDs
        const isUploadFailure = error.message === 'No files were uploaded successfully.' || 
                               error.message === 'No valid document UUIDs found from uploaded files.' ||
                               !uploadResult ||
                               (uploadResult && uploadResult.successful === 0);
        
        if (isUploadFailure) {
          // Upload failed
          const errorMessage = error.message || 'An error occurred while uploading files.';
          toast.error({
            title: 'Upload Failed',
            message: errorMessage,
          });
          handleUploadError(error);
        } else {
          // Upload succeeded but attach failed before we could process
          toast.error({
            title: 'Files Uploaded',
            message: 'Documents could not be linked to the knowledge base. Please try linking them later.',
          });
          setUploadError('Documents could not be linked to the knowledge base. Please try linking them later.');
          setIsProcessing(false);
        }
      }
    }
  }, [processFiles, attachFiletoKnowledge, toast, handleCloseAndNavigate, handleUploadError]);

  const handleFileChange = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Clear input
    event.target.value = '';

    // Start upload + attach process
    await handleUploadAndAttach(files);
  }, [handleUploadAndAttach]);

  const handleDrop = useCallback(async (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length === 0) return;

    // Start upload + attach process
    await handleUploadAndAttach(files);
  }, [handleUploadAndAttach]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium" width={modalWidth}>
      <Modal.Header>
        <Modal.Header.Title>
         Upload Facts
        </Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalBodyContent>
          <input {...fileInputProps} onChange={handleFileChange} />
          <DragAndDrop
            className="upload-facts-modal-drag-and-drop"
            uploadType="documents"
            documentUploadStyle="minimalist"
            title="Drop files here or click to upload"
            description="Supports PDF, DOCX, TXT, CSV and more"
            width="100%"
            onUploadClick={openFileSelector}
            onDrop={handleDrop}
          />
          {uploadError && (
            <div style={{ color: 'red', marginTop: '8px' }}>
              {uploadError}
            </div>
          )}
        </ModalBodyContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Modal.Button
          label="Cancel"
          onClick={onClose}
          type="tertiary"
        />
        <Modal.Button
          label={isProcessing ? 'Processing...' : 'Upload'}
          onClick={openFileSelector}
          type="primary"
          disabled={isProcessing}
        />
      </Modal.Actions>
    </Modal>
  );
});

UploadFactsModal.displayName = 'UploadFactsModal';

export default UploadFactsModal;
